import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast, Toaster } from 'sonner';
import { 
  Shield, AlertTriangle, Users, FileText, Bell, 
  ArrowRight, Loader2, Play, Sparkles, Lock, Brain
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ReactMarkdown from 'react-markdown';
import AIDemoUnlockModal from '../components/AIDemoUnlockModal';
import { useBooking } from '../context/BookingContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Icon mapping for CMS
const iconMap = {
  Shield, AlertTriangle, Users, FileText, Bell, Brain, Sparkles, Lock
};

const AIDemoPage = () => {
  const { openBooking, openContact } = useBooking();
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [activeDemo, setActiveDemo] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoResult, setDemoResult] = useState(null);
  
  // CMS settings state
  const [cmsSettings, setCmsSettings] = useState(null);

  // Fetch CMS settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/cms/ai-demo-settings`);
        if (response.ok) {
          const data = await response.json();
          setCmsSettings(data);
        }
      } catch (error) {
        console.error('Failed to fetch AI demo settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Get CMS values with fallbacks
  const heroTitle = cmsSettings?.hero_title || 'Experience AI-Powered NDIS Management';
  const heroSubtitle = cmsSettings?.hero_subtitle || 'Try our intelligent features that help you stay compliant, reduce admin time, and deliver better care';
  const heroBadge = cmsSettings?.hero_badge || 'Live AI Demo';
  const ctaTitle = cmsSettings?.cta_title || 'Ready to Transform Your Operations?';
  const ctaSubtitle = cmsSettings?.cta_subtitle || 'See how these AI features work with your actual data';
  const ctaButtonText = cmsSettings?.cta_button_text || 'Book a Personalised Demo';

  // Check if user is a returning user who has already submitted any form
  const isReturningUser = () => {
    return (
      localStorage.getItem('contact_submitted') === 'true' ||
      localStorage.getItem('booking_submitted') === 'true' ||
      localStorage.getItem('demo_submitted') === 'true' ||
      localStorage.getItem('pricing_unlocked') === 'true' ||
      localStorage.getItem('ai_lead_id') !== null
    );
  };

  // Check localStorage on mount - unlock for any returning user
  useEffect(() => {
    setIsUnlocked(isReturningUser());
  }, []);

  // Demo inputs
  const [incidentText, setIncidentText] = useState('A participant experienced a fall during morning transfer. Staff were present but unable to prevent the incident. Minor bruising noted. Family has been notified.');
  const [staffCount, setStaffCount] = useState(50);
  const [serviceType, setServiceType] = useState('SIL');

  const features = [
    { 
      id: 'compliance', 
      icon: Shield, 
      title: 'AI Compliance Engine', 
      desc: 'Automated NDIS document compliance checking',
      color: 'tech-blue'
    },
    { 
      id: 'incidents', 
      icon: AlertTriangle, 
      title: 'Incident Pattern Detection', 
      desc: 'AI-powered incident analysis and risk prediction',
      color: 'heritage-orange'
    },
    { 
      id: 'workforce', 
      icon: Users, 
      title: 'Predictive Workforce Planning', 
      desc: 'Smart staffing predictions and optimization',
      color: 'care-teal'
    },
    { 
      id: 'reports', 
      icon: FileText, 
      title: 'AI Executive Reports', 
      desc: 'Automated KPI reports and executive summaries',
      color: 'purple-500'
    },
    { 
      id: 'notifications', 
      icon: Bell, 
      title: 'Smart Notifications', 
      desc: 'Intelligent alerts and action recommendations',
      color: 'pink-500'
    }
  ];

  const runDemo = async (featureId) => {
    // Check if unlocked
    if (!isUnlocked) {
      toast.error('Please unlock the AI demo first');
      setShowUnlockModal(true);
      return;
    }

    setActiveDemo(featureId);
    setDemoLoading(true);
    setDemoResult(null);

    try {
      let endpoint, body;
      const leadId = localStorage.getItem('ai_lead_id') || localStorage.getItem('contact_email') || 'demo_user';
      
      switch(featureId) {
        case 'compliance':
          endpoint = '/api/ai/compliance-check';
          body = { lead_id: leadId, document_type: 'policy' };
          break;
        case 'incidents':
          endpoint = '/api/ai/incident-analysis';
          body = { lead_id: leadId, incident_description: incidentText };
          break;
        case 'workforce':
          endpoint = '/api/ai/workforce-prediction';
          body = { lead_id: leadId, staff_count: staffCount, service_type: serviceType };
          break;
        case 'reports':
          endpoint = '/api/ai/executive-report';
          body = { lead_id: leadId, report_type: 'monthly' };
          break;
        case 'notifications':
          endpoint = '/api/ai/smart-notification';
          body = { lead_id: leadId, notification_type: 'compliance' };
          break;
        default:
          throw new Error('Unknown feature');
      }

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Demo failed');
      }

      const data = await res.json();
      setDemoResult(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDemoLoading(false);
    }
  };

  const getDemoContent = (result) => {
    if (!result) return null;
    return result.analysis || result.prediction || result.report || result.notification;
  };

  const handleUnlockSuccess = () => {
    setIsUnlocked(true);
  };

  return (
    <div className="min-h-screen bg-page-bg">
      <SEOHead 
        title="AI Demo | GetCare 360 - Try NDIS AI Features Live"
        description="Experience GetCare 360's AI-powered features live. Try our compliance engine, incident pattern detection, and workforce planning tools in real-time."
        keywords="NDIS AI demo, disability care AI features, NDIS compliance AI, incident detection software demo"
        canonicalUrl="/ai-demo"
        page="ai-demo"
      />
      <Toaster position="top-right" richColors />
      <Header />
      
      {/* AI Demo Unlock Modal */}
      <AIDemoUnlockModal 
        isOpen={showUnlockModal} 
        onClose={() => setShowUnlockModal(false)} 
        onSuccess={handleUnlockSuccess}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-navy via-[#0d2847] to-primary-navy relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-tech-blue rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-care-teal rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6">
              <Sparkles className="text-care-teal mr-2" size={18} />
              <span className="text-sm font-medium text-white">{heroBadge} - Powered by GPT-5.2</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {heroTitle.includes('AI') ? (
                <>Try Our <span className="text-care-teal">AI Features</span> Live</>
              ) : (
                heroTitle
              )}
            </h1>
            
            <p className="text-xl text-white/80 max-w-3xl mx-auto mb-8">
              {heroSubtitle}
            </p>

            {!isUnlocked && (
              <Button 
                onClick={() => setShowUnlockModal(true)}
                className="bg-gradient-to-r from-purple-600 to-care-teal hover:from-purple-600/90 hover:to-care-teal/90 text-white px-8 py-6 text-lg"
                data-testid="unlock-demo-btn"
              >
                <Brain className="mr-2" size={20} />
                Unlock AI Demo
                <ArrowRight className="ml-2" size={20} />
              </Button>
            )}

            {isUnlocked && (
              <div className="inline-flex items-center px-4 py-2 bg-care-teal/20 rounded-full border border-care-teal/30">
                <span className="text-care-teal font-medium">✓ Demo access granted - Select a feature below</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Gated Content - Show locked state if not unlocked */}
      {!isUnlocked ? (
        <section className="py-20 bg-surface">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-page-bg rounded-2xl border-2 border-dashed border-border-line p-12">
              <div className="w-24 h-24 bg-gradient-to-br from-purple-500/20 to-care-teal/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="text-purple-600" size={48} />
              </div>
              <h2 className="text-2xl font-bold text-text-primary mb-4">
                AI Features Are Locked
              </h2>
              <p className="text-text-secondary mb-8 max-w-xl mx-auto">
                Unlock access to our live AI demos. Experience real-time compliance checking, incident analysis, workforce predictions, and more.
              </p>
              
              {/* Preview of features */}
              <div className="grid md:grid-cols-3 gap-4 mb-8 opacity-60">
                {features.slice(0, 3).map((feature) => {
                  const Icon = feature.icon;
                  return (
                    <div key={feature.id} className="bg-surface p-4 rounded-xl border border-border-line">
                      <Icon className="text-gray-400 mx-auto mb-2" size={24} />
                      <p className="text-sm text-gray-500">{feature.title}</p>
                    </div>
                  );
                })}
              </div>

              <Button 
                onClick={() => setShowUnlockModal(true)}
                className="bg-gradient-to-r from-purple-600 to-care-teal hover:from-purple-600/90 hover:to-care-teal/90 text-white px-8 py-6 text-lg"
                data-testid="contact-unlock-btn"
              >
                <Brain className="mr-2" size={20} />
                Unlock AI Demo
              </Button>
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* Feature Cards - Only visible after contact submission */}
          <section className="py-16 bg-surface">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10">
                <h2 className="text-2xl font-bold text-text-primary mb-2">Select an AI Feature to Demo</h2>
                <p className="text-text-secondary">Click on any feature card to see it in action</p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {features.map((feature) => {
                  const Icon = feature.icon;
                  const isActive = activeDemo === feature.id;
                  
                  return (
                    <Card 
                      key={feature.id}
                      className={`border-2 transition-all cursor-pointer hover:shadow-lg ${
                        isActive ? 'border-care-teal ring-2 ring-care-teal/30' : 'border-border-line hover:border-tech-blue/50'
                      }`}
                      onClick={() => !demoLoading && runDemo(feature.id)}
                      data-testid={`demo-card-${feature.id}`}
                    >
                      <CardContent className="p-6">
                        <div className="w-14 h-14 rounded-xl bg-tech-blue/10 flex items-center justify-center mb-4">
                          <Icon className="text-tech-blue" size={28} />
                        </div>
                        <h3 className="text-xl font-bold text-text-primary mb-2">{feature.title}</h3>
                        <p className="text-text-secondary mb-4">{feature.desc}</p>
                        <Button 
                          variant="outline" 
                          className="w-full"
                          disabled={demoLoading && isActive}
                        >
                          {demoLoading && isActive ? (
                            <><Loader2 className="animate-spin mr-2" size={16} /> Generating...</>
                          ) : (
                            <><Play className="mr-2" size={16} /> Try Demo</>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Demo Input & Result Section */}
          {activeDemo && (
            <section className="py-16 bg-page-bg" data-testid="demo-result-section">
              <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Demo-specific inputs */}
                {activeDemo === 'incidents' && (
                  <Card className="mb-6 border-border-line">
                    <CardHeader>
                      <CardTitle className="text-lg">Customize Incident Description</CardTitle>
                      <CardDescription>Edit the sample incident or enter your own scenario</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <textarea
                        rows={4}
                        value={incidentText}
                        onChange={(e) => setIncidentText(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-border-line bg-surface text-text-primary"
                        placeholder="Describe the incident..."
                      />
                      <Button 
                        onClick={() => runDemo('incidents')}
                        className="mt-3 bg-heritage-orange hover:bg-heritage-orange/90"
                        disabled={demoLoading}
                      >
                        {demoLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Play className="mr-2" size={16} />}
                        Re-analyze Incident
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {activeDemo === 'workforce' && (
                  <Card className="mb-6 border-border-line">
                    <CardHeader>
                      <CardTitle className="text-lg">Customize Workforce Parameters</CardTitle>
                      <CardDescription>Adjust staff count and service type for your scenario</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-text-secondary mb-2">Staff Count</label>
                          <Input
                            type="number"
                            value={staffCount}
                            onChange={(e) => setStaffCount(parseInt(e.target.value) || 50)}
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-text-secondary mb-2">Service Type</label>
                          <select
                            value={serviceType}
                            onChange={(e) => setServiceType(e.target.value)}
                            className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                          >
                            <option value="SIL">SIL Provider</option>
                            <option value="Support Coordination">Support Coordination</option>
                            <option value="Allied Health">Allied Health</option>
                            <option value="Plan Management">Plan Management</option>
                          </select>
                        </div>
                      </div>
                      <Button 
                        onClick={() => runDemo('workforce')}
                        className="mt-4 bg-care-teal hover:bg-care-teal/90"
                        disabled={demoLoading}
                      >
                        {demoLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Play className="mr-2" size={16} />}
                        Re-generate Prediction
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Result Display */}
                {demoResult && (
                  <Card className="border-2 border-care-teal/50 bg-surface" data-testid="ai-result-card">
                    <CardHeader className="bg-care-teal/5 border-b border-border-line">
                      <div className="flex items-center space-x-2">
                        <Sparkles className="text-care-teal" size={20} />
                        <CardTitle className="text-lg">AI Analysis Result</CardTitle>
                      </div>
                      <CardDescription>
                        Generated at {new Date(demoResult.analyzed_at || demoResult.generated_at).toLocaleString()}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="prose prose-sm max-w-none text-text-primary prose-headings:text-text-primary prose-strong:text-text-primary">
                        <ReactMarkdown>{getDemoContent(demoResult)}</ReactMarkdown>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {demoLoading && !demoResult && (
                  <Card className="border-2 border-border-line bg-surface">
                    <CardContent className="p-12 text-center">
                      <Loader2 className="animate-spin mx-auto mb-4 text-care-teal" size={48} />
                      <p className="text-text-secondary">AI is analyzing your request...</p>
                      <p className="text-sm text-text-secondary mt-2">This may take a few seconds</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </section>
          )}
        </>
      )}

      {/* CTA */}
      <section className="py-20 bg-primary-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {ctaTitle.includes('Transform') ? (
              <>Ready for <span className="text-care-teal">AI-Powered</span> NDIS Management?</>
            ) : (
              ctaTitle
            )}
          </h2>
          <p className="text-xl text-white/80 mb-8">
            {ctaSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openBooking} className="bg-care-teal hover:bg-care-teal/90 text-white px-8 py-6 text-lg">
                {ctaButtonText}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            <Button onClick={openContact} variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Contact Us
              </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIDemoPage;
